import React, { useState, useEffect, useCallback } from 'react'
import { connect, useSelector } from 'react-redux'
import { setLoading, setSnackInfo } from '../../redux/actions/appAction'
import HttpService from '../../services/httpService'
import {
    Typography, Paper, Grid, Button, Table, TableBody, TableCell, TableContainer, TableRow, TableHead,
    TextField, Checkbox, Chip, CircularProgress, TablePagination,
    IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions
} from '@material-ui/core'
import EditOutlinedIcon from '@material-ui/icons/EditOutlined'
import { makeStyles } from '@material-ui/core/styles'
import Autocomplete from '@material-ui/lab/Autocomplete'
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank'
import CheckBoxIcon from '@material-ui/icons/CheckBox'
import SecurityIcon from '@material-ui/icons/Security'
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline'
import HistoryIcon from '@material-ui/icons/History'
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline'
import PersonOutlineIcon from '@material-ui/icons/PersonOutline'
import SupervisorAccountIcon from '@material-ui/icons/SupervisorAccount'
import BusinessIcon from '@material-ui/icons/Business'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import CancelIcon from '@material-ui/icons/Cancel'
import { ROLENAME } from '../../utils'

const GRANT_TYPE_LABELS = {
    user: 'User',
    role: 'Role',
    department: 'Department',
}

const GRANT_TYPE_ICONS = {
    user: PersonOutlineIcon,
    role: SupervisorAccountIcon,
    department: BusinessIcon,
}

const GRANT_TYPE_COLORS = {
    user: { backgroundColor: '#e3f2fd', color: '#1565c0' },
    role: { backgroundColor: '#ede7f6', color: '#5e35b1' },
    department: { backgroundColor: '#fff3e0', color: '#ef6c00' },
}

const LEAD_SCOPE_LABELS = {
    specific: 'Specific leads',
    all: 'All leads',
    city: 'By city',
    experienceCenter: 'By experience center',
}

const uncheckedIcon = <CheckBoxOutlineBlankIcon fontSize="small" />
const checkedIcon = <CheckBoxIcon fontSize="small" />

const leadOptionLabel = (lead) => !lead ? '' : `${lead.uniqueLeadNumber || lead.lead_no}${lead.customerId?.name ? ` — ${lead.customerId.name}` : ''}`

const useStyles = makeStyles(() => ({
    page: {
        padding: 24,
        maxWidth: 1400,
        margin: '0 auto',
    },
    headerCard: {
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        marginBottom: 28,
    },
    headerIcon: {
        width: 46,
        height: 46,
        borderRadius: 12,
        backgroundColor: '#1C375D',
        color: '#FFF2EB',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
    },
    title: {
        fontSize: 24,
        fontWeight: 700,
        color: '#2b2b2b',
        margin: 0,
    },
    subtitle: {
        fontSize: 14,
        color: '#777',
        marginTop: 2,
        maxWidth: 760,
    },
    sectionCard: {
        padding: 24,
        borderRadius: 12,
        boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
        marginBottom: 32,
    },
    sectionHeaderRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 12,
        marginBottom: 18,
    },
    sectionTitleWrap: {
        display: 'flex',
        alignItems: 'center',
        gap: 10,
    },
    sectionTitle: {
        fontSize: 18,
        fontWeight: 700,
        color: '#1C375D',
        margin: 0,
    },
    sectionSubtitle: {
        fontSize: 13,
        color: '#888',
        marginTop: 2,
    },
    countBadge: {
        backgroundColor: '#eef1f6',
        color: '#1C375D',
        borderRadius: 16,
        padding: '3px 12px',
        fontSize: 13,
        fontWeight: 700,
    },
    addButton: {
        backgroundColor: '#355025',
        color: '#FFF2EB',
        textTransform: 'none',
        fontWeight: 600,
        borderRadius: 8,
        boxShadow: 'none',
        '&:hover': {
            backgroundColor: '#28401c',
            boxShadow: 'none',
        },
    },
    tableContainer: {
        borderRadius: 8,
        boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
    },
    headCell: {
        padding: 12,
        fontWeight: 700,
        backgroundColor: '#1C375D',
        color: '#FFF2EB',
        whiteSpace: 'nowrap',
    },
    bodyCell: {
        padding: 12,
        verticalAlign: 'top',
    },
    rowHover: {
        '&:hover': {
            backgroundColor: '#f5f7f2',
        },
    },
    emptyState: {
        padding: '32px 16px',
        textAlign: 'center',
        color: '#999',
    },
    statusChip: {
        fontWeight: 600,
    },
    filterBar: {
        marginBottom: 18,
    },
}))

// Debounced server-side search of leads by uniqueLeadNumber / lead_no — shared
// by every lead picker on this page, so nobody has to type/paste a raw Mongo
// _id (which was throwing a cast error on the filter fields).
function useLeadSearch() {
    const [input, setInput] = useState('')
    const [options, setOptions] = useState([])
    const [loading, setLoading] = useState(false)

    useEffect(() => {
        if (!input || input.trim().length < 2) {
            setOptions([])
            return
        }
        setLoading(true)
        const timer = setTimeout(async () => {
            try {
                const res = await HttpService.searchLeadsForAccessGrant(input.trim())
                setOptions(res.data || [])
            } catch (error) {
                setOptions([])
            }
            setLoading(false)
        }, 350)
        return () => clearTimeout(timer)
    }, [input])

    return { input, setInput, options, loading }
}

// A single-select "search leads by unique lead number" Autocomplete, used for
// the grants/logs filter fields (as opposed to the multi-select picker used
// when creating a grant).
function LeadFilterAutocomplete({ label, value, onChange, search }) {
    return (
        <Autocomplete
            loading={search.loading}
            options={search.options}
            filterOptions={(x) => x}
            getOptionLabel={leadOptionLabel}
            getOptionSelected={(option, val) => option._id === val._id}
            value={value}
            inputValue={search.input}
            onInputChange={(e, newInput, reason) => { if (reason !== 'reset') search.setInput(newInput) }}
            onChange={(e, newValue) => onChange(newValue)}
            noOptionsText={search.input.trim().length < 2 ? 'Type at least 2 characters…' : 'No leads found'}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={label}
                    variant="outlined"
                    size="small"
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {search.loading ? <CircularProgress color="inherit" size={16} /> : null}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                />
            )}
        />
    )
}

// Shows the first item as a chip plus a "+N" chip; hovering reveals the full list.
function CompactChips({ items, chipStyle, variant, prefix }) {
    if (!items || items.length === 0) return <span style={{ color: '#bbb' }}>—</span>
    const first = prefix ? `${prefix}: ${items[0]}` : items[0]
    return (
        <Tooltip
            interactive
            title={
                <div style={{ maxHeight: 240, overflowY: 'auto' }}>
                    {prefix && <div style={{ fontWeight: 700, marginBottom: 4 }}>{prefix} ({items.length})</div>}
                    {items.map((it, i) => <div key={i}>{i + 1}. {it}</div>)}
                </div>
            }
        >
            <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'nowrap', cursor: 'default' }}>
                <Chip size="small" variant={variant} style={{ maxWidth: 140, ...chipStyle }} label={first} />
                {items.length > 1 && <Chip size="small" style={{ marginLeft: 4 }} label={`+${items.length - 1}`} />}
            </div>
        </Tooltip>
    )
}

function LeadScopeCell({ grant }) {
    if (grant.leadIds?.length > 0) {
        return <CompactChips items={grant.leadIds.map(l => String(l.uniqueLeadNumber || l.lead_no || l))} />
    }
    if (grant.leadCityIds?.length > 0) {
        return <CompactChips prefix="City" chipStyle={{ backgroundColor: '#e0f2f1', color: '#00695c' }} items={grant.leadCityIds.map(c => String(c.name || c))} />
    }
    if (grant.leadExperienceCenterIds?.length > 0) {
        return <CompactChips prefix="Exp Center" chipStyle={{ backgroundColor: '#e0f2f1', color: '#00695c' }} items={grant.leadExperienceCenterIds.map(e => String(e.name || e))} />
    }
    return <Chip size="small" style={{ backgroundColor: '#1C375D', color: '#FFF2EB', fontWeight: 700 }} label="ALL LEADS" />
}

// Admin-only screen for the LeadAccessGrant overrides: lets an Admin grant one
// or more users / roles / departments / cities / experience centers extra
// access to one or more leads' (or every lead's) project logs, on top of the
// default assignee/manager-chain rule enforced by GET /lead/leadDetails.
const LeadAccessGrants = (props) => {
    const classes = useStyles()
    const { userDetails } = useSelector(state => state.user)
    const isAdmin = userDetails?.roles?.some(role => role.name === ROLENAME.Admin)

    const [leadScopeType, setLeadScopeType] = useState('specific') // 'specific' | 'all' | 'city' | 'experienceCenter'
    const [selectedLeads, setSelectedLeads] = useState([]) // [{ _id, lead_no, uniqueLeadNumber, customerId }]
    const specificLeadSearch = useLeadSearch()
    const [selectedLeadCities, setSelectedLeadCities] = useState([])
    const [selectedLeadExpCenters, setSelectedLeadExpCenters] = useState([])

    const [filterLead, setFilterLead] = useState(null)
    const grantsFilterSearch = useLeadSearch()

    const [grantType, setGrantType] = useState('user')
    const [targetIds, setTargetIds] = useState([])
    const [note, setNote] = useState('')

    const [grantSearch, setGrantSearch] = useState('')
    const [grantDeptFilter, setGrantDeptFilter] = useState(null)
    const [grantsPage, setGrantsPage] = useState(0)
    const [grantsRowsPerPage, setGrantsRowsPerPage] = useState(10)
    const [editingId, setEditingId] = useState(null)
    const [grantToDelete, setGrantToDelete] = useState(null)

    const [users, setUsers] = useState([])
    const [roles, setRoles] = useState([])
    const [departments, setDepartments] = useState([])
    const [cities, setCities] = useState([])
    const [experienceCenters, setExperienceCenters] = useState([])
    const [grants, setGrants] = useState([])

    const [logs, setLogs] = useState([])
    const [logsTotal, setLogsTotal] = useState(0)
    const [logsPage, setLogsPage] = useState(0)
    const [logsRowsPerPage, setLogsRowsPerPage] = useState(25)
    const [logsLeadFilter, setLogsLeadFilter] = useState(null)
    const logsFilterSearch = useLeadSearch()
    const [showLogs, setShowLogs] = useState(false)

    useEffect(() => {
        if (!isAdmin) return
        HttpService.getAllUsers().then(res => setUsers(res.data || [])).catch(() => {})
        HttpService.getAllRoles().then(res => setRoles(res.data || [])).catch(() => {})
        HttpService.getDepartments().then(res => setDepartments(res.data || [])).catch(() => {})
        HttpService.getAllLocations().then(res => setCities(res.data || [])).catch(() => {})
        HttpService.getExperinceCenter().then(res => setExperienceCenters(res.data || [])).catch(() => {})
    }, [isAdmin])

    const loadGrants = useCallback(async () => {
        if (!isAdmin) return
        try {
            const res = await HttpService.getLeadAccessGrants(filterLead?._id || undefined)
            setGrants(res.data?.data || [])
        } catch (error) {
            props.setSnackInfo(error?.response?.data?.message ?? 'Failed to load grants', 'error')
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAdmin, filterLead])

    useEffect(() => { loadGrants() }, [loadGrants])

    const loadLogs = useCallback(async () => {
        if (!isAdmin || !showLogs) return
        try {
            const res = await HttpService.getLeadAccessLogs({
                leadId: logsLeadFilter?._id || undefined,
                page: logsPage + 1,
                limit: logsRowsPerPage,
            })
            setLogs(res.data?.data || [])
            setLogsTotal(res.data?.totalItems || 0)
        } catch (error) {
            props.setSnackInfo(error?.response?.data?.message ?? 'Failed to load access logs', 'error')
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAdmin, showLogs, logsLeadFilter, logsPage, logsRowsPerPage])

    useEffect(() => { loadLogs() }, [loadLogs])

    const addGrant = async () => {
        const leadIds = leadScopeType === 'specific' ? selectedLeads.map(l => l._id) : []
        const leadCityIds = leadScopeType === 'city' ? selectedLeadCities.map(c => c._id) : []
        const leadExperienceCenterIds = leadScopeType === 'experienceCenter' ? selectedLeadExpCenters.map(e => e._id) : []

        if (leadScopeType === 'specific' && leadIds.length === 0) {
            props.setSnackInfo('Search and select one or more leads', 'error')
            return
        }
        if (leadScopeType === 'city' && leadCityIds.length === 0) {
            props.setSnackInfo('Select one or more cities', 'error')
            return
        }
        if (leadScopeType === 'experienceCenter' && leadExperienceCenterIds.length === 0) {
            props.setSnackInfo('Select one or more experience centers', 'error')
            return
        }
        if (targetIds.length === 0) {
            props.setSnackInfo(`Select at least one ${GRANT_TYPE_LABELS[grantType]}`, 'error')
            return
        }
        props.setLoading(true)
        try {
            const payload = { leadIds, leadCityIds, leadExperienceCenterIds, grantType, targetIds, note }
            if (editingId) {
                await HttpService.updateLeadAccessGrant(editingId, payload)
                props.setSnackInfo('Access grant updated', 'success')
            } else {
                await HttpService.createLeadAccessGrant(payload)
                props.setSnackInfo('Access grant added', 'success')
            }
            resetForm()
            loadGrants()
        } catch (error) {
            props.setSnackInfo(error?.response?.data?.message ?? `Failed to ${editingId ? 'update' : 'add'} grant`, 'error')
        }
        props.setLoading(false)
    }

    const resetForm = () => {
        setEditingId(null)
        setLeadScopeType('specific')
        setGrantType('user')
        setTargetIds([])
        setNote('')
        setSelectedLeads([])
        setSelectedLeadCities([])
        setSelectedLeadExpCenters([])
    }

    const startEdit = (g) => {
        setEditingId(g._id)
        if (g.leadIds?.length > 0) setLeadScopeType('specific')
        else if (g.leadCityIds?.length > 0) setLeadScopeType('city')
        else if (g.leadExperienceCenterIds?.length > 0) setLeadScopeType('experienceCenter')
        else setLeadScopeType('all')
        setSelectedLeads(g.leadIds || [])
        setSelectedLeadCities(g.leadCityIds || [])
        setSelectedLeadExpCenters(g.leadExperienceCenterIds || [])
        setGrantType(g.grantType)
        setTargetIds(g.targetIds || [])
        setNote(g.note || '')
        window.scrollTo({ top: 0, behavior: 'smooth' })
    }

    const confirmRemoveGrant = async () => {
        const id = grantToDelete?._id
        setGrantToDelete(null)
        if (!id) return
        props.setLoading(true)
        try {
            await HttpService.deleteLeadAccessGrant(id)
            props.setSnackInfo('Access grant removed', 'success')
            if (editingId === id) resetForm()
            loadGrants()
        } catch (error) {
            props.setSnackInfo(error?.response?.data?.message ?? 'Failed to remove grant', 'error')
        }
        props.setLoading(false)
    }

    if (!isAdmin) {
        return (
            <div style={{ padding: '60px 24px', textAlign: 'center' }}>
                <Typography variant="h5" gutterBottom>Access Denied</Typography>
                <Typography variant="body1">Only Admin can manage lead access grants.</Typography>
            </div>
        )
    }

    const targetOptionsByType = { user: users, role: roles, department: departments, city: cities, experienceCenter: experienceCenters }
    const userDeptId = (u) => (u.departmentId?._id || u.departmentId || u.department?._id)
    const userDeptName = (u) => u.departmentId?.name || u.department?.name
    const baseTargetOptions = targetOptionsByType[grantType] || []
    const targetOptions = baseTargetOptions
    const optionLabel = (type, opt) => {
        if (!opt) return ''
        if (type !== 'user') return opt.name
        const dept = userDeptName(opt)
        const roleNames = (opt.roles || []).map(r => r.name).filter(Boolean).join(', ')
        const extra = [dept, roleNames].filter(Boolean).join(' · ')
        return `${opt.name} (${opt.email})${extra ? ` — ${extra}` : ''}`
    }
    const labelFor = (type, id) => {
        const opt = (targetOptionsByType[type] || []).find(o => o._id === id)
        return opt ? optionLabel(type, opt) : id
    }
    const GrantTypeIcon = GRANT_TYPE_ICONS[grantType]

    // Client-side search over the active grants: target name / email / department, note, creator
    const grantMatches = (g) => {
        const targets = (g.targetIds || []).map(id => (targetOptionsByType[g.grantType] || []).find(o => o._id === id)).filter(Boolean)
        if (grantDeptFilter) {
            const inDept = g.grantType === 'department'
                ? (g.targetIds || []).includes(grantDeptFilter._id)
                : g.grantType === 'user' && targets.some(u => userDeptId(u) === grantDeptFilter._id)
            if (!inDept) return false
        }
        const q = grantSearch.trim().toLowerCase()
        if (!q) return true
        const haystack = [
            ...targets.flatMap(t => [t.name, t.email, userDeptName(t), ...(t.roles || []).map(r => r.name)]),
            g.note, g.createdBy?.name, g.createdBy?.email,
        ].filter(Boolean).join(' ').toLowerCase()
        return haystack.includes(q)
    }
    const filteredGrants = grants.filter(grantMatches)
    const maxGrantsPage = Math.max(0, Math.ceil(filteredGrants.length / grantsRowsPerPage) - 1)
    const safeGrantsPage = Math.min(grantsPage, maxGrantsPage)
    const pagedGrants = filteredGrants.slice(safeGrantsPage * grantsRowsPerPage, (safeGrantsPage + 1) * grantsRowsPerPage)

    return (
        <div className={classes.page}>
            <div className={classes.headerCard}>
                <div className={classes.headerIcon}><SecurityIcon /></div>
                <div style={{ flex: 1 }}>
                    <h1 className={classes.title}>Lead Access Grants</h1>
                    <div className={classes.subtitle}>
                        Give users, roles, or departments extra access to project logs — by specific leads, city, or
                        experience center — on top of the default rule (sales/design assignee + their manager chain).
                    </div>
                </div>
                <Button
                    variant={showLogs ? 'outlined' : 'contained'}
                    startIcon={showLogs ? <SecurityIcon /> : <HistoryIcon />}
                    onClick={() => setShowLogs(v => !v)}
                    style={showLogs
                        ? { textTransform: 'none', borderColor: '#1C375D', color: '#1C375D' }
                        : { textTransform: 'none', backgroundColor: '#1C375D', color: '#FFF2EB' }}
                >
                    {showLogs ? 'Back to Grants' : 'View Access Logs'}
                </Button>
            </div>

            {!showLogs && (
            <>
            {/* Create grant */}
            <Paper className={classes.sectionCard}>
                <div className={classes.sectionHeaderRow}>
                    <div className={classes.sectionTitleWrap}>
                        <AddCircleOutlineIcon style={{ color: '#355025' }} />
                        <h2 className={classes.sectionTitle}>{editingId ? 'Edit Access Grant' : 'Create Access Grant'}</h2>
                    </div>
                </div>

                <Grid container spacing={2} alignItems="flex-start">
                    <Grid item xs={12} sm={2}>
                        <Autocomplete
                            options={Object.keys(LEAD_SCOPE_LABELS)}
                            getOptionLabel={(type) => LEAD_SCOPE_LABELS[type]}
                            value={leadScopeType}
                            disableClearable
                            onChange={(e, newValue) => setLeadScopeType(newValue)}
                            renderInput={(params) => <TextField {...params} label="Lead scope" variant="outlined" size="small" />}
                        />
                    </Grid>
                    <Grid item xs={12} sm={5}>
                        {leadScopeType === 'specific' && (
                            <>
                                <Autocomplete
                                    multiple
                                    disableCloseOnSelect
                                    loading={specificLeadSearch.loading}
                                    options={specificLeadSearch.options}
                                    filterOptions={(x) => x}
                                    getOptionLabel={leadOptionLabel}
                                    getOptionSelected={(option, value) => option._id === value._id}
                                    value={selectedLeads}
                                    inputValue={specificLeadSearch.input}
                                    onInputChange={(e, newInput, reason) => { if (reason !== 'reset') specificLeadSearch.setInput(newInput) }}
                                    onChange={(e, newValue) => setSelectedLeads(newValue)}
                                    noOptionsText={specificLeadSearch.input.trim().length < 2 ? 'Type at least 2 characters…' : 'No leads found'}
                                    renderOption={(option, { selected }) => (
                                        <>
                                            <Checkbox icon={uncheckedIcon} checkedIcon={checkedIcon} checked={selected} style={{ marginRight: 8 }} />
                                            {leadOptionLabel(option)}
                                        </>
                                    )}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Search leads by unique lead number"
                                            variant="outlined"
                                            size="small"
                                            InputProps={{
                                                ...params.InputProps,
                                                endAdornment: (
                                                    <>
                                                        {specificLeadSearch.loading ? <CircularProgress color="inherit" size={16} /> : null}
                                                        {params.InputProps.endAdornment}
                                                    </>
                                                ),
                                            }}
                                        />
                                    )}
                                />
                                <Typography variant="caption" color="textSecondary">{selectedLeads.length} lead(s) selected</Typography>
                            </>
                        )}
                        {leadScopeType === 'city' && (
                            <Autocomplete
                                multiple
                                disableCloseOnSelect
                                options={cities}
                                getOptionLabel={(opt) => opt.name}
                                value={selectedLeadCities}
                                onChange={(e, newValue) => setSelectedLeadCities(newValue)}
                                renderOption={(option, { selected }) => (
                                    <>
                                        <Checkbox icon={uncheckedIcon} checkedIcon={checkedIcon} checked={selected} style={{ marginRight: 8 }} />
                                        {option.name}
                                    </>
                                )}
                                renderInput={(params) => <TextField {...params} label="Search cities" variant="outlined" size="small" />}
                            />
                        )}
                        {leadScopeType === 'experienceCenter' && (
                            <Autocomplete
                                multiple
                                disableCloseOnSelect
                                options={experienceCenters}
                                getOptionLabel={(opt) => opt.name}
                                value={selectedLeadExpCenters}
                                onChange={(e, newValue) => setSelectedLeadExpCenters(newValue)}
                                renderOption={(option, { selected }) => (
                                    <>
                                        <Checkbox icon={uncheckedIcon} checkedIcon={checkedIcon} checked={selected} style={{ marginRight: 8 }} />
                                        {option.name}
                                    </>
                                )}
                                renderInput={(params) => <TextField {...params} label="Search experience centers" variant="outlined" size="small" />}
                            />
                        )}
                        {leadScopeType === 'all' && (
                            <Typography variant="body2" color="textSecondary" style={{ paddingTop: 8 }}>
                                This grant will apply to every lead.
                            </Typography>
                        )}
                    </Grid>
                    <Grid item xs={12} sm={2}>
                        <Autocomplete
                            options={Object.keys(GRANT_TYPE_LABELS)}
                            getOptionLabel={(type) => GRANT_TYPE_LABELS[type]}
                            value={grantType}
                            disableClearable
                            onChange={(e, newValue) => { setGrantType(newValue); setTargetIds([]) }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Grant type"
                                    variant="outlined"
                                    size="small"
                                    InputProps={{
                                        ...params.InputProps,
                                        startAdornment: GrantTypeIcon ? <GrantTypeIcon fontSize="small" style={{ color: '#888', marginRight: 4 }} /> : null,
                                    }}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <Autocomplete
                            multiple
                            disableCloseOnSelect
                            options={targetOptions}
                            getOptionLabel={(opt) => optionLabel(grantType, opt)}
                            value={targetOptions.filter(o => targetIds.includes(o._id))}
                            onChange={(e, newValue) => setTargetIds(newValue.map(o => o._id))}
                            renderOption={(option, { selected }) => (
                                <>
                                    <Checkbox icon={uncheckedIcon} checkedIcon={checkedIcon} checked={selected} style={{ marginRight: 8 }} />
                                    {optionLabel(grantType, option)}
                                </>
                            )}
                            renderInput={(params) => (
                                <TextField {...params} label={grantType === 'user' ? 'Search users by name / email' : `Search ${GRANT_TYPE_LABELS[grantType]}(s)`} variant="outlined" size="small" />
                            )}
                        />
                    </Grid>

                    <Grid item xs={12} sm={8}>
                        <TextField label="Note (optional)" fullWidth variant="outlined" size="small" value={note} onChange={e => setNote(e.target.value)} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <div style={{ display: 'flex', gap: 8 }}>
                            <Button
                                className={classes.addButton}
                                variant="contained"
                                fullWidth
                                startIcon={editingId ? <EditOutlinedIcon /> : <AddCircleOutlineIcon />}
                                onClick={addGrant}
                            >
                                {editingId ? 'Update Access Grant' : 'Add Access Grant'}
                            </Button>
                            {editingId && (
                                <Button variant="outlined" onClick={resetForm} style={{ textTransform: 'none' }}>
                                    Cancel
                                </Button>
                            )}
                        </div>
                    </Grid>
                </Grid>
            </Paper>

            {/* Active grants */}
            <div className={classes.sectionHeaderRow}>
                <div className={classes.sectionTitleWrap}>
                    <SecurityIcon style={{ color: '#1C375D' }} />
                    <h2 className={classes.sectionTitle}>Active Grants</h2>
                    <span className={classes.countBadge}>{filteredGrants.length}{filteredGrants.length !== grants.length ? ` / ${grants.length}` : ''}</span>
                </div>
            </div>

            <Grid container spacing={2} alignItems="center" className={classes.filterBar}>
                <Grid item xs={12} sm={3}>
                    <TextField
                        label="Search by name / email"
                        fullWidth
                        variant="outlined"
                        size="small"
                        value={grantSearch}
                        onChange={e => { setGrantSearch(e.target.value); setGrantsPage(0) }}
                    />
                </Grid>
                <Grid item xs={12} sm={3}>
                    <Autocomplete
                        options={departments}
                        getOptionLabel={(opt) => opt.name || ''}
                        getOptionSelected={(option, val) => option._id === val._id}
                        value={grantDeptFilter}
                        onChange={(e, newValue) => { setGrantDeptFilter(newValue); setGrantsPage(0) }}
                        renderInput={(params) => <TextField {...params} label="Filter by department" variant="outlined" size="small" />}
                    />
                </Grid>
                <Grid item xs={12} sm={5}>
                    <LeadFilterAutocomplete
                        label="Filter grants by lead (optional)"
                        value={filterLead}
                        onChange={(v) => { setFilterLead(v); setGrantsPage(0) }}
                        search={grantsFilterSearch}
                    />
                </Grid>
            </Grid>

            <TableContainer component={Paper} className={classes.tableContainer} style={{ marginBottom: 40 }}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell className={classes.headCell}>Sr No.</TableCell>
                            <TableCell className={classes.headCell}>Scope (leads)</TableCell>
                            <TableCell className={classes.headCell}>Type</TableCell>
                            <TableCell className={classes.headCell}>Targets</TableCell>
                            <TableCell className={classes.headCell}>Note</TableCell>
                            <TableCell className={classes.headCell}>Created By</TableCell>
                            <TableCell className={classes.headCell} align="right">Action</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {pagedGrants.map((g, idx) => {
                            const RowIcon = GRANT_TYPE_ICONS[g.grantType]
                            return (
                                <TableRow key={g._id} className={classes.rowHover}>
                                    <TableCell className={classes.bodyCell}>{safeGrantsPage * grantsRowsPerPage + idx + 1}</TableCell>
                                    <TableCell className={classes.bodyCell}><LeadScopeCell grant={g} /></TableCell>
                                    <TableCell className={classes.bodyCell}>
                                        <Chip
                                            size="small"
                                            icon={RowIcon ? <RowIcon fontSize="small" /> : undefined}
                                            style={GRANT_TYPE_COLORS[g.grantType] || {}}
                                            label={GRANT_TYPE_LABELS[g.grantType] || g.grantType}
                                        />
                                    </TableCell>
                                    <TableCell className={classes.bodyCell}>
                                        <CompactChips variant="outlined" items={(g.targetIds || []).map(id => String(labelFor(g.grantType, id)))} />
                                    </TableCell>
                                    <TableCell className={classes.bodyCell}>
                                        {g.note ? (
                                            <Tooltip title={g.note} interactive>
                                                <span style={{ display: 'block', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'default' }}>{g.note}</span>
                                            </Tooltip>
                                        ) : <span style={{ color: '#bbb' }}>—</span>}
                                    </TableCell>
                                    <TableCell className={classes.bodyCell}>{g.createdBy?.name}</TableCell>
                                    <TableCell className={classes.bodyCell} align="right">
                                        <Tooltip title="Edit this grant">
                                            <IconButton size="small" onClick={() => startEdit(g)}>
                                                <EditOutlinedIcon fontSize="small" style={{ color: '#1C375D' }} />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Revoke this grant">
                                            <IconButton size="small" onClick={() => setGrantToDelete(g)}>
                                                <DeleteOutlineIcon fontSize="small" style={{ color: '#c62828' }} />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            )
                        })}
                        {filteredGrants.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={7} className={classes.emptyState}>No grants found</TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
                <TablePagination
                    component="div"
                    count={filteredGrants.length}
                    page={safeGrantsPage}
                    onChangePage={(e, newPage) => setGrantsPage(newPage)}
                    rowsPerPage={grantsRowsPerPage}
                    onChangeRowsPerPage={e => { setGrantsRowsPerPage(parseInt(e.target.value, 10)); setGrantsPage(0) }}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                />
            </TableContainer>
            </>
            )}

            {showLogs && (
                <>
                    <div className={classes.sectionHeaderRow}>
                        <div className={classes.sectionTitleWrap}>
                            <HistoryIcon style={{ color: '#1C375D' }} />
                            <h2 className={classes.sectionTitle}>Lead Access Logs</h2>
                            <span className={classes.countBadge}>{logsTotal}</span>
                        </div>
                    </div>
                    <Typography variant="body2" className={classes.sectionSubtitle} style={{ marginBottom: 18 }}>
                        Every denied attempt to open a project log — who tried, and which lead — so you can decide whether to grant them access.
                    </Typography>

                    <Grid container spacing={2} alignItems="center" className={classes.filterBar}>
                        <Grid item xs={12} sm={4}>
                            <LeadFilterAutocomplete
                                label="Filter by lead (optional)"
                                value={logsLeadFilter}
                                onChange={(newValue) => { setLogsLeadFilter(newValue); setLogsPage(0) }}
                                search={logsFilterSearch}
                            />
                        </Grid>
                    </Grid>

                    <TableContainer component={Paper} className={classes.tableContainer}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell className={classes.headCell}>When</TableCell>
                                    <TableCell className={classes.headCell}>User</TableCell>
                                    <TableCell className={classes.headCell}>Department</TableCell>
                                    <TableCell className={classes.headCell}>Lead</TableCell>
                                    <TableCell className={classes.headCell}>Status</TableCell>
                                    <TableCell className={classes.headCell}>Reason</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {logs.map(l => (
                                    <TableRow key={l._id} className={classes.rowHover}>
                                        <TableCell className={classes.bodyCell}>{new Date(l.createdAt).toLocaleString()}</TableCell>
                                        <TableCell className={classes.bodyCell}>
                                            <div>{l.userId?.name}</div>
                                            {l.userId?.email && <div style={{ fontSize: 12, color: '#999' }}>{l.userId.email}</div>}
                                        </TableCell>
                                        <TableCell className={classes.bodyCell}>{l.departmentId?.name || '-'}</TableCell>
                                        <TableCell className={classes.bodyCell}>{l.leadId?.uniqueLeadNumber || l.leadId?.lead_no || l.leadId}</TableCell>
                                        <TableCell className={classes.bodyCell}>
                                            <Chip
                                                size="small"
                                                className={classes.statusChip}
                                                icon={l.allowed ? <CheckCircleIcon style={{ color: '#1e7e34' }} /> : <CancelIcon style={{ color: '#c62828' }} />}
                                                label={l.allowed ? 'Allowed' : 'Denied'}
                                                style={l.allowed
                                                    ? { backgroundColor: '#e6f4ea', color: '#1e7e34' }
                                                    : { backgroundColor: '#fdecea', color: '#c62828' }}
                                            />
                                        </TableCell>
                                        <TableCell className={classes.bodyCell}>{l.reason}</TableCell>
                                    </TableRow>
                                ))}
                                {logs.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={6} className={classes.emptyState}>No access logs found</TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                        <TablePagination
                            component="div"
                            count={logsTotal}
                            page={logsPage}
                            onChangePage={(e, newPage) => setLogsPage(newPage)}
                            rowsPerPage={logsRowsPerPage}
                            onChangeRowsPerPage={e => { setLogsRowsPerPage(parseInt(e.target.value, 10)); setLogsPage(0) }}
                            rowsPerPageOptions={[25, 50, 100]}
                        />
                    </TableContainer>
                </>
            )}

            <Dialog open={!!grantToDelete} onClose={() => setGrantToDelete(null)} maxWidth="xs" fullWidth>
                <DialogTitle>Revoke access grant?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        This will remove the access for{' '}
                        {(grantToDelete?.targetIds || []).map(id => labelFor(grantToDelete.grantType, id)).join(', ') || 'the selected targets'}.
                        They will no longer be able to open the project logs granted by this entry.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setGrantToDelete(null)} style={{ textTransform: 'none' }}>Cancel</Button>
                    <Button onClick={confirmRemoveGrant} variant="contained" style={{ textTransform: 'none', backgroundColor: '#c62828', color: '#fff' }}>
                        Yes, Revoke
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    )
}

export default connect(null, { setLoading, setSnackInfo })(LeadAccessGrants)
